// filtri/tipi.ts — il contratto tra il motore dei filtri e ogni singolo filtro.
// Il motore (filtri/motore.ts) prende il fotogramma della fotocamera, lo fa passare dall'eventuale shader del
// filtro (colore o deformazione), poi chiama `disegna` per gli elementi sopra il volto, e manda il risultato
// all'avversario. Il filtro non tocca mai la fotocamera, la rete, il DOM o il resto dell'app.
//
// QUESTO FILE NON SI MODIFICA nei filtri: se a un filtro serve qualcosa che qui non c'è, lo si chiede.
import type { PelleFiltro } from "./pelle.ts";
export type { PelleFiltro, MappaPelle, PuntoTrama } from "./pelle.ts";

/** Piano in cui il filtro è disponibile. "esclusivo" = si compra con i crediti, non è in nessun piano. */
export type PianoFiltro = "base" | "silver" | "esclusivo";

/** Prezzi ammessi per gli esclusivi (in crediti). */
export const PREZZI_CREDITI = [10, 25, 50, 100, 250, 500, 1000] as const;
export type PrezzoCrediti = (typeof PREZZI_CREDITI)[number];

export const CATEGORIE_FILTRO = [
  "testa",
  "occhi",
  "naso-bocca",
  "barba-baffi",
  "animali",
  "deformazione",
  "colore",
  "mestieri",
  "reattivi",
  "scenici",
  "stagionali",
] as const;
export type CategoriaFiltro = (typeof CATEGORIE_FILTRO)[number];

/** Un punto del volto in pixel del fotogramma d'uscita (non specchiato). z: profondità relativa, più piccolo = più vicino. */
export interface Punto {
  x: number;
  y: number;
  z: number;
}

/** Le 52 espressioni di MediaPipe (blendshape), 0..1. Nomi esatti di MediaPipe: "mouthSmileLeft", "jawOpen", "eyeBlinkLeft", ... */
export type Espressioni = Readonly<Record<string, number>>;

/** Posizione della testa, già calcolata dal motore (vedi geometria.ts → posaDa). */
export interface Posa {
  /** Rotazione nel piano dell'immagine, radianti (positivo = testa inclinata in senso orario sull'immagine). */
  rollio: number;
  /** Rotazione destra/sinistra, radianti, stima (0 = di fronte, positivo = il naso va verso il lato destro dell'immagine). */
  imbardata: number;
  /** Rotazione su/giù, radianti, stima (0 = di fronte, positivo = testa alzata). */
  beccheggio: number;
  /** Distanza in pixel tra i centri dei due occhi: l'unità di misura di tutto ciò che si disegna sul volto. */
  scala: number;
}

export interface Volto {
  /** I 478 punti di MediaPipe Face Landmarker (468 del volto + 10 delle iridi), indici in punti.ts. */
  punti: readonly Punto[];
  espressioni: Espressioni;
  posa: Posa;
  /** Matrice 4x4 (colonna per colonna) volto → fotocamera di MediaPipe, per chi disegna in 3D. Può mancare. */
  matrice: Float32Array | null;
}

/** Valore di un parametro regolabile dal pannello di amministrazione. */
export type ValoreParametro = number | boolean | string;

export interface ContestoFiltro {
  larghezza: number;
  altezza: number;
  /** Millisecondi dall'attivazione del filtro (monotono). */
  tempoMs: number;
  /** Millisecondi dal fotogramma precedente (limitato a 100 per non far "saltare" le animazioni dopo una pausa). */
  dtMs: number;
  /** null = nessun volto in questo fotogramma: il filtro non disegna nulla sul volto (può far svanire ciò che aveva). */
  volto: Volto | null;
  /** Valori correnti dei parametri dichiarati nel manifest (già validati e con il predefinito se mancano). */
  parametri: Readonly<Record<string, ValoreParametro>>;
  /** Il telefono chiede meno movimento: niente oscillazioni continue, niente lampi, particelle ridotte. */
  riduciAnimazioni: boolean;
  /** Numeri casuali 0..1 riproducibili (seme fisso per le prove): MAI Math.random() nei filtri. */
  casuale: () => number;
}

/** Ciò che il motore mette a disposizione in `prepara`. */
export interface RisorseFiltro {
  /** Carica un file della cartella risorse/ del filtro (es. "baffi.svg"): pronto da passare a drawImage. */
  immagine(nome: string): Promise<CanvasImageSource>;
}

/**
 * Passaggio sul fotogramma intero (colore o deformazione), in GLSL ES 3.00.
 * `codice` contiene SOLO la funzione `vec4 effetto(vec2 uv)`, più eventuali funzioni di aiuto e uniform proprie.
 * Il motore aggiunge davanti le uniform e le funzioni comuni descritte in docs/filtri/SPECIFICA-FILTRI.md §5.
 */
export interface PassaggioShader {
  codice: string;
  /** Valori delle uniform proprie del filtro per questo fotogramma (nome → numero o vettore di 2..4 numeri). */
  uniformi?(c: ContestoFiltro): Record<string, number | readonly number[]>;
}

export interface Filtro {
  /** Uguale all'id del manifest. */
  readonly id: string;
  /** Carica le immagini. Si chiama una volta prima del primo fotogramma; massimo 2 secondi. */
  prepara?(r: RisorseFiltro): Promise<void>;
  /** Passaggio sul fotogramma (facoltativo): colore, deformazione. */
  readonly shader?: PassaggioShader;
  /**
   * v043: «pelle» (facoltativa): una trama disegnata sulla mappa del viso che il motore incolla su tutto il volto e
   * fa seguire ogni movimento (trasformare la faccia, non solo metterci sopra qualcosa). Vedi pelle.ts e
   * docs/filtri/SPECIFICA-FILTRI-WOW.md. Si disegna sotto `disegna`.
   */
  readonly pelle?: PelleFiltro;
  /**
   * v043: quanta bocca deve restare libera. "tutta" (predefinito): tutta la zona della bocca. "sotto": il filtro può
   * coprire la parte alta dell'apertura (dentoni, zanne, becco che scende sul labbro); resta libera la parte bassa,
   * dove si vede la risata.
   */
  readonly bocca?: "tutta" | "sotto";
  /**
   * Disegna sopra il fotogramma (facoltativo). Il contesto arriva pulito: trasformazione identità, alpha 1,
   * nessuna ombra; il motore lo salva e lo ripristina attorno alla chiamata. Budget: 4 ms su un telefono medio.
   */
  disegna?(ctx: CanvasRenderingContext2D, c: ContestoFiltro): void;
  /** Libera tutto (immagini, cache). Dopo, il filtro non viene più usato. */
  libera?(): void;
}

/** Un parametro regolabile dal pannello di amministrazione (settings). */
export type DefinizioneParametro =
  | { tipo: "numero"; predefinito: number; min: number; max: number; passo?: number }
  | { tipo: "booleano"; predefinito: boolean }
  | { tipo: "colore"; predefinito: string }; // "#rrggbb"

export interface ManifestFiltro {
  id: string;
  numero: number;
  categoria: CategoriaFiltro;
  piano: PianoFiltro;
  /** Solo per "esclusivo"; 0 negli altri piani. */
  prezzoCrediti: 0 | PrezzoCrediti;
  /** Tecniche usate: O = disegno sopra, D = deformazione, C = colore, R = reagisce alle espressioni. */
  tecniche: ("O" | "D" | "C" | "R")[];
  /** Espressioni lette (nomi MediaPipe), per sapere cosa fa reagire il filtro. */
  espressioniUsate: string[];
  parametri: Record<string, DefinizioneParametro>;
  /** Peso totale della cartella risorse/, in byte (massimo 150 000). */
  pesoRisorse: number;
  versione: number;
}

/** Ogni filtro esporta di default una funzione che crea un'istanza nuova (stato interno separato per ogni uso). */
export type CreaFiltro = () => Filtro;
